import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  Bell, BellOff, AlertTriangle, Zap, UserPlus, Clock,
  CheckCircle, RefreshCw, ChevronLeft, ChevronRight, Filter
} from 'lucide-react';

const ALERT_TYPE_CONFIG = {
  error_spike: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
  slow_api: { icon: Zap, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  new_tenant: { icon: UserPlus, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  inactive_tenant: { icon: Clock, color: 'text-slate-400', bg: 'bg-slate-500/10' },
};

const SEVERITY_COLORS = {
  critical: 'border-red-500/30 bg-red-500/5',
  warning: 'border-amber-500/30 bg-amber-500/5',
  info: 'border-blue-500/30 bg-blue-500/5',
};

export default function AdminAlertsPage() {
  const { authHeaders, API } = useAdminAuth();
  const { t } = useTranslation();
  const { lang } = useTranslation().i18n;
  const isAr = lang === 'ar';
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isRead, setIsRead] = useState('false');
  const [alertType, setAlertType] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const [alertRes, statsRes] = await Promise.all([
        axios.get(`${API}/alerts`, { ...authHeaders(), params: { is_read: isRead, alert_type: alertType, page, limit: 20 } }),
        axios.get(`${API}/alerts/stats`, authHeaders()),
      ]);
      setAlerts(alertRes.data.alerts);
      setTotal(alertRes.data.total);
      setStats(statsRes.data);
    } catch {}
    setLoading(false);
  }, [API, authHeaders, isRead, alertType, page]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/alerts/${id}/read`, {}, authHeaders());
      fetchAlerts();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/alerts/read-all`, {}, authHeaders());
      fetchAlerts();
    } catch {}
  };

  const getAlertTypeLabel = (type) => {
    const map = {
      error_spike: t('admin.alertsPage.errorSpike'),
      slow_api: t('admin.alertsPage.slowApi'),
      new_tenant: t('admin.alertsPage.newTenant'),
    };
    return map[type] || type;
  };

  const totalPages = Math.ceil(total / 20);
  const s = stats || {};

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-alerts-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{t('admin.alertsPage.title')}</h1>
            <p className="text-sm text-slate-500">{t('admin.alertsPage.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn"
              className="text-slate-400 hover:text-white text-xs">
              <CheckCircle className="w-3.5 h-3.5 me-1" /> {t('admin.alertsPage.markAllRead')}
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchAlerts} data-testid="refresh-alerts-btn"
              className="text-slate-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl px-4 py-3">
            <p className="text-lg font-bold text-white">{s.total || 0}</p>
            <p className="text-[10px] text-slate-500">{t('admin.alertsPage.totalAlerts')}</p>
          </div>
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl px-4 py-3">
            <p className={`text-lg font-bold ${s.unread > 0 ? 'text-orange-400' : 'text-slate-300'}`}>{s.unread || 0}</p>
            <p className="text-[10px] text-slate-500">{t('admin.alertsPage.unread')}</p>
          </div>
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl px-4 py-3">
            <p className={`text-lg font-bold ${s.critical > 0 ? 'text-red-400' : 'text-slate-300'}`}>{s.critical || 0}</p>
            <p className="text-[10px] text-slate-500">{t('admin.alertsPage.criticalUnread')}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap items-center">
          <Filter className="w-4 h-4 text-slate-500" />
          {[
            { val: 'false', label: t('admin.alertsPage.unreadFilter') },
            { val: 'true', label: t('admin.alertsPage.read') },
            { val: '', label: t('admin.alertsPage.allFilter') },
          ].map(f => (
            <Button key={f.val} size="sm" variant="ghost"
              onClick={() => { setIsRead(f.val); setPage(1); }}
              className={`text-xs h-7 ${isRead === f.val ? 'bg-slate-800 text-white' : 'text-slate-400'}`}>
              {f.label}
            </Button>
          ))}
          <div className="w-px h-4 bg-slate-800 mx-1" />
          {[
            { val: '', label: t('admin.alertsPage.allTypes') },
            { val: 'error_spike', label: t('admin.alertsPage.errorSpike') },
            { val: 'slow_api', label: t('admin.alertsPage.slowApi') },
            { val: 'new_tenant', label: t('admin.alertsPage.newTenant') },
          ].map(f => (
            <Button key={f.val} size="sm" variant="ghost"
              data-testid={`filter-type-${f.val || 'all'}`}
              onClick={() => { setAlertType(f.val); setPage(1); }}
              className={`text-xs h-7 ${alertType === f.val ? 'bg-slate-800 text-white' : 'text-slate-400'}`}>
              {f.label}
            </Button>
          ))}
        </div>

        {/* Alert List */}
        <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BellOff className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('admin.alertsPage.noAlerts')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/40">
              {alerts.map(alert => {
                const typeConfig = ALERT_TYPE_CONFIG[alert.type] || ALERT_TYPE_CONFIG.error_spike;
                const TypeIcon = typeConfig.icon;
                const sevColor = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
                return (
                  <div
                    key={alert.id}
                    data-testid={`alert-row-${alert.id}`}
                    className={`px-4 py-3 transition-colors ${!alert.is_read ? sevColor : 'hover:bg-slate-800/20'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg ${typeConfig.bg} flex items-center justify-center flex-shrink-0`}>
                        <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${!alert.is_read ? 'text-white' : 'text-slate-400'}`}>
                            {isAr ? (alert.title_ar || alert.title_en) : (alert.title_en || alert.title_ar)}
                          </p>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${typeConfig.bg} ${typeConfig.color}`}>
                            {getAlertTypeLabel(alert.type)}
                          </span>
                          {!alert.is_read && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {isAr ? (alert.message_ar || alert.message_en) : (alert.message_en || alert.message_ar)}
                        </p>
                        <p className="text-[10px] text-slate-600 mt-1">{new Date(alert.timestamp).toLocaleString()}</p>
                      </div>
                      {!alert.is_read && (
                        <Button size="sm" variant="ghost" onClick={() => markRead(alert.id)}
                          data-testid={`mark-read-${alert.id}`}
                          className="text-slate-500 hover:text-white text-xs h-7 flex-shrink-0">
                          <CheckCircle className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800/60">
              <span className="text-xs text-slate-500">{t('admin.tenants.page')} {page} {t('admin.tenants.of')} {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="text-slate-400 h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="text-slate-400 h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
